import React, { useEffect, useMemo, useState } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import Dashboard from './components/dashboard/Dashboard';
import Leads from './components/leads/Leads';
import Clients from './components/clients/Clients';
import Invoices from './components/invoices/Invoices';
import Projects from './components/projects/Projects';
import Settings from './components/settings/Settings';
import Templates from './components/settings/Templates';
import Proposals from './components/proposals/Proposals';
import Documents from './components/documents/Documents';
import Messages from './components/messages/Messages';
import UsersManagement from './components/users/Users';
import Financials from './components/financials/Financials';
import Reviews from './components/reviews/Reviews';
import { Code, Activity, Bot, Users, FileText, Settings as SettingsIcon, LogOut, Eye, EyeOff, CheckCircle, MessageSquare, DollarSign } from 'lucide-react';

const input = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };

function Login() {
  const { login, acceptStaffInvite, loading, error } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [localError, setLocalError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const inviteData = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('staffInvite') || '';
    const inviteEmail = params.get('email') || '';
    return { token, email: inviteEmail, active: Boolean(token && inviteEmail) };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSuccessMsg('');

    try {
      if (inviteData.active) {
        if (!password || password.length < 6) return setLocalError('Password must be at least 6 characters');
        if (password !== confirmPassword) return setLocalError('Passwords do not match');
        await acceptStaffInvite(inviteData.email, inviteData.token, password);
        window.history.replaceState({}, document.title, window.location.pathname);
        setSuccessMsg('Invite accepted. Signing in...');
        return;
      }

      await login(email, password);
    } catch (err) {
      setLocalError(err.response?.data?.error || (inviteData.active ? 'Failed to accept invite' : 'Login failed'));
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #020617, #0f172a)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <div style={{ width: '100%', maxWidth: '28rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src="/logos/pagehyve-icon-color.svg" alt="PageHyve Studios" style={{ width: '4rem', height: '4rem', margin: '0 auto 1rem' }} />
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Admin Dashboard</h1>
          <p style={{ color: '#94a3b8' }}>{inviteData.active ? 'Set your password to activate staff access' : 'Sign in to access admin panel'}</p>
        </div>
        <form onSubmit={handleSubmit} style={{ background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem', padding: '2rem' }}>
          {inviteData.active ? (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Invited Email</label>
              <input type="email" value={inviteData.email} readOnly style={{ ...input, opacity: 0.9 }} />
            </div>
          ) : (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required style={input} placeholder="admin@pagehyve.com" />
            </div>
          )}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>{inviteData.active ? 'New Password' : 'Password'}</label>
            <div style={{ position: 'relative' }}>
              <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required autoComplete={inviteData.active ? 'new-password' : 'current-password'} style={{ ...input, paddingRight: '3rem' }} placeholder="********" />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                {showPw ? <EyeOff style={{ width: '1.25rem', height: '1.25rem' }} /> : <Eye style={{ width: '1.25rem', height: '1.25rem' }} />}
              </button>
            </div>
          </div>

          {inviteData.active && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Confirm Password</label>
              <input type={showPw ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required autoComplete="new-password" style={input} placeholder="********" />
            </div>
          )}

          {(localError || error) && <div style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '0.5rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', fontSize: '0.875rem' }}>{localError || error}</div>}
          {successMsg && <div style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '0.5rem', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#34d399', fontSize: '0.875rem' }}>{successMsg}</div>}
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '0.875rem 1.5rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', opacity: loading ? 0.5 : 1 }}>
            {loading ? (inviteData.active ? 'Activating...' : 'Signing in...') : (inviteData.active ? 'Activate Account' : 'Sign In')}
          </button>
          {/*<p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#64748b', marginTop: '1rem' }}>Default: admin@pagehyve.com / Admin123!@#</p>*/}
        </form>
      </div>
    </div>
  );
}

function AdminLayout() {
  const { currentSection, setCurrentSection, logout, user, notificationSummary, fetchNotificationSummary } = useApp();
  const [showSidebar, setShowSidebar] = useState(true);
  const isStaff = user?.role === 'staff';
  const docsSeenKey = `adminDocsSeenAt:${user?.id || user?._id || user?.email || 'unknown'}`;

  useEffect(() => {
    if (!user) return;
    const documentsSince = localStorage.getItem(docsSeenKey) || undefined;
    fetchNotificationSummary({ documentsSince });
    const intervalId = setInterval(() => {
      const nextSince = localStorage.getItem(docsSeenKey) || undefined;
      fetchNotificationSummary({ documentsSince: nextSince });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [user, docsSeenKey, fetchNotificationSummary]);

  useEffect(() => {
    if (currentSection === 'documents') {
      const nowIso = new Date().toISOString();
      localStorage.setItem(docsSeenKey, nowIso);
      fetchNotificationSummary({ documentsSince: nowIso });
      return;
    }
    const documentsSince = localStorage.getItem(docsSeenKey) || undefined;
    fetchNotificationSummary({ documentsSince });
  }, [currentSection, docsSeenKey, fetchNotificationSummary]);

  const navItems = isStaff
    ? [
        { id: 'dashboard', icon: Activity, label: 'Dashboard' },
        { id: 'projects', icon: CheckCircle, label: 'Projects' },
        { id: 'messages', icon: MessageSquare, label: 'Messages', count: notificationSummary?.messagesUnread || 0 },
        { id: 'settings', icon: SettingsIcon, label: 'Settings' },
      ]
    : [
        { id: 'dashboard', icon: Activity, label: 'Dashboard' },
        { id: 'leads', icon: Bot, label: 'AI Lead Gen', badge: 'AI' },
        { id: 'clients', icon: Users, label: 'Clients' },
        { id: 'projects', icon: CheckCircle, label: 'Projects' },
        { id: 'invoices', icon: FileText, label: 'Invoices' },
        { id: 'proposals', icon: FileText, label: 'Proposals' },
        { id: 'documents', icon: FileText, label: 'Documents', count: notificationSummary?.documentsNew || 0 },
        { id: 'messages', icon: MessageSquare, label: 'Messages', count: notificationSummary?.messagesUnread || 0 },
        { id: 'users', icon: Users, label: 'Users' },
        { id: 'financials', icon: DollarSign, label: 'Financials' },
        { id: 'reviews', icon: MessageSquare, label: 'Reviews', count: notificationSummary?.reviewsPending || 0 },
        { id: 'templates', icon: FileText, label: 'Templates' },
        { id: 'settings', icon: SettingsIcon, label: 'Settings' },
      ];

  const renderContent = () => {
    if (isStaff && !['dashboard', 'projects', 'messages', 'settings'].includes(currentSection)) {
      return <Dashboard />;
    }
    switch (currentSection) {
      case 'leads': return <Leads />;
      case 'clients': return <Clients />;
      case 'projects': return <Projects />;
      case 'invoices': return <Invoices />;
      case 'proposals': return <Proposals />;
      case 'documents': return <Documents />;
      case 'messages': return <Messages />;
      case 'users': return <UsersManagement />;
      case 'financials': return <Financials />;
      case 'reviews': return <Reviews />;
      case 'templates': return <Templates />;
      case 'settings': return <Settings />;
      default: return <Dashboard />;
    }
  };

  // Hide sidebar automatically on small screens when a nav item is selected
  const onNavClick = (id) => {
    setCurrentSection(id);
    if (window.innerWidth < 900) setShowSidebar(false);
  };

  return (
    <div className="app-shell" style={{ minHeight: '100vh', display: 'flex', background: '#020617' }}>
      <aside className={`app-aside ${showSidebar ? 'show' : ''}`} style={{ width: '16rem', background: 'rgba(15,23,42,0.5)', borderRight: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img src="/logos/pagehyve-icon-color.svg" alt="PageHyve Studios" style={{ width: '2.5rem', height: '2.5rem' }} />
            <div><span style={{ fontWeight: 700, display: 'block' }}>PageHyve</span><p style={{ fontSize: '0.75rem', color: '#64748b' }}>{isStaff ? 'Team Workspace' : 'Admin Panel'}</p></div>
          </div>
        </div>
        <nav style={{ flex: 1, padding: '1rem' }}>
          {navItems.map(item => (
            <button key={item.id} onClick={() => onNavClick(item.id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderRadius: '0.75rem', marginBottom: '0.25rem', background: currentSection === item.id ? 'linear-gradient(to r, rgba(16,185,129,0.2), rgba(6,182,212,0.2))' : 'transparent', color: currentSection === item.id ? '#34d399' : '#94a3b8', textAlign: 'left' }}>
              <item.icon style={{ width: '1.25rem', height: '1.25rem' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{item.label}</span>
              {item.badge && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: 'rgba(16,185,129,0.2)', color: '#34d399' }}>{item.badge}</span>}
              {Number(item.count) > 0 && (
                <span style={{ marginLeft: 'auto', minWidth: '1.25rem', height: '1.25rem', borderRadius: '999px', background: '#ef4444', color: '#fff', fontSize: '0.7rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.35rem' }}>
                  {item.count > 99 ? '99+' : item.count}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div style={{ padding: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <button onClick={() => { logout(); if (window.innerWidth < 900) setShowSidebar(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', color: '#94a3b8' }}>
            <LogOut style={{ width: '1.25rem', height: '1.25rem' }} />
            <span style={{ fontSize: '0.875rem' }}>Logout</span>
          </button>
        </div>
      </aside>

      {/* floating toggle for small screens */}
      <button className="sidebar-toggle" onClick={() => setShowSidebar(s => !s)} aria-label="Toggle sidebar">Menu</button>

      {/* backdrop to close sidebar on small screens */}
      {showSidebar && (typeof window !== 'undefined' && window.innerWidth < 900) && (
        <div className="sidebar-backdrop" onClick={() => setShowSidebar(false)} />
      )}

      <main style={{ flex: 1, overflow: 'auto' }}>{renderContent()}</main>
    </div>
  );
}

function AppContent() {
  const { isAuthenticated } = useApp();
  return isAuthenticated ? <AdminLayout /> : <Login />;
}

export default function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}


